<template>
  <div class="qty-selector">
    <div class="label" :class="{ disabled }" v-if="label">{{ label }}</div>
    <div>
      <button class="qty-control minus" @click="$emit('reduce-qty')"
        :class="{ disabled: quantity - quantityStep < minQuantity || disabled }">
        <i class="fa-light fa-sharp fa-minus"></i>
      </button>

      <input v-if="typeable" type="number" class="qty" :value="localQuantity" @input="handleInput" @change="handleChange" @blur="handleBlur" max="99" min="0"
        :disabled="disabled" ref="inputRef" />
      <input v-else type="number" class="qty no-typing" :value="quantity" max="99" min="0" disabled />

      <button class="qty-control plus" :class="{ disabled }" @click="$emit('increase-qty')">
        <i class="fa-light fa-sharp fa-plus"></i>
      </button>
    </div>

    <slot></slot>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';

const props = defineProps({
  label: String,
  quantity: {
    type: Number,
    required: true
  },
  disabled: Boolean,
  minQuantity: {
    type: Number,
    default: 0
  },
  quantityStep: {
    type: Number,
    default: 1
  },
  typeable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['increase-qty', 'reduce-qty', 'set-quantity']);

// Template refs
const inputRef = ref(null);

// Local quantity that user can type in freely
const localQuantity = ref(props.quantity);

// Track if user is actively typing
let isTyping = false;

// Watch for external changes to quantity prop (only update if not typing)
watch(() => props.quantity, (newValue) => {
  if (!isTyping) {
    localQuantity.value = newValue;
  }
});

// Handle input events (while typing)
function handleInput(event) {
  isTyping = true;
  localQuantity.value = event.target.value;
  
  // Clear existing timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  
  // Set up debounced update
  debounceTimer = setTimeout(() => {
    const numValue = parseInt(event.target.value, 10);
    if (!isNaN(numValue) && numValue !== props.quantity) {
      emit('set-quantity', numValue);
    }
    isTyping = false;
  }, 500);
}

// Handle change events (when user stops typing/editing)
function handleChange(event) {
  const numValue = parseInt(event.target.value, 10);
  if (!isNaN(numValue) && numValue !== props.quantity) {
    // Clear debounce timer and emit immediately
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    emit('set-quantity', numValue);
  }
  isTyping = false;
}

// Handle blur events (when user clicks away)
function handleBlur(event) {
  const numValue = parseInt(event.target.value, 10);
  if (!isNaN(numValue) && numValue !== props.quantity) {
    // Clear debounce timer and emit immediately
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    emit('set-quantity', numValue);
  }
  isTyping = false;
}

// Debounce timer
let debounceTimer = null;

// Cleanup on unmount
onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});
</script>
